import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

// Alias for the DOM KeyboardEvent — the React import above shadows the
// global, but the global handler attached to `window` needs the DOM type.
type DomKeyboardEvent = globalThis.KeyboardEvent;

import { suggestArticles } from "../api/articles";
import { useSession } from "../auth/useSession";
import type { ArticleSuggestion } from "../api/articles";
import { useDebouncedValue } from "../lib/useDebouncedValue";
import ArticleSuggestionContent from "./ArticleSuggestionContent";
import { fetchBelege } from "../api/belege";
import { formatDateShort } from "../lib/format";
import { ApiError } from "../lib/api";

const RESULT_LIMIT = 10;

const IS_APPLE_PLATFORM =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/i.test(navigator.platform || "");

const SHORTCUT_LABEL = IS_APPLE_PLATFORM ? "⌘K" : "Ctrl K";

// The overview searches both existing APIs. Other entry points stay article-only.
export default function ArticleSearch({
  belegeSearch = false,
  placeholder = "Artikel suchen …",
  ariaLabel = "Artikel suchen",
  prominent = false,
  enableShortcut = true,
}: {
  belegeSearch?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  prominent?: boolean;
  enableShortcut?: boolean;
} = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isFetching: checkingAccess } = useSession();
  const searchScope = [user?.customer_id, user?.user_id];
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounced = useDebouncedValue(query, 200);
  const trimmed = debounced.trim();

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: ["articles", "suggest", ...searchScope, trimmed],
    queryFn: () => suggestArticles(trimmed, RESULT_LIMIT),
    enabled: Boolean(user) && !checkingAccess && trimmed.length > 0,
  });

  const currentQuery = query.trim();
  const isDebouncing = currentQuery !== trimmed;
  const items = useMemo<ArticleSuggestion[]>(
    () => (trimmed && !isDebouncing && !isFetching && !isError ? (data?.items ?? []) : []),
    [data, isDebouncing, isFetching, isError, trimmed],
  );
  const documents = useQuery({
    queryKey: ["belege", "quick-search", ...searchScope, trimmed],
    queryFn: () => fetchBelege({ q: trimmed, limit: 5 }),
    enabled: Boolean(user) && !checkingAccess && belegeSearch && trimmed.length > 0,
  });
  const busy = !user || checkingAccess || isDebouncing || isFetching || (belegeSearch && documents.isFetching);
  const documentItems = belegeSearch && !isDebouncing && !documents.isFetching && !documents.isError ? documents.data?.items ?? [] : [];
  const selectableCount = busy ? 0 : items.length + documentItems.length;
  const hasError = isError || (belegeSearch && documents.isError);

  function editQuery() {
    inputRef.current?.focus();
    inputRef.current?.select();
  }

  function selectDocument(id: string) {
    setIsOpen(false);
    setQuery("");
    navigate(`/belege/${encodeURIComponent(id)}`);
  }

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  useEffect(() => {
    if (!isOpen || busy) return;
    containerRef.current?.querySelector(`[data-search-option="${highlight}"]`)?.scrollIntoView({ block: "nearest" });
  }, [highlight, isOpen, busy]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    window.addEventListener("mousedown", handleClickOutside);
    return () => window.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Globaler Shortcut: Cmd/Ctrl+K fokussiert die Suche von jeder Seite.
  // Nicht greifen, wenn der User gerade in einem anderen Eingabefeld
  // tippt — sonst würde es Editierfluss zerstören.
  useEffect(() => {
    if (!enableShortcut) return;

    function handleShortcut(event: DomKeyboardEvent) {
      const isShortcut =
        (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k";
      if (!isShortcut) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      const inEditableField =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        Boolean(target?.isContentEditable);
      // Im Suchfeld selbst trotzdem refokussieren erlauben.
      if (inEditableField && target !== inputRef.current) return;
      event.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    }
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [enableShortcut]);

  function selectArticle(article: ArticleSuggestion) {
    setIsOpen(false);
    setQuery("");
    navigate(`/articles/${encodeURIComponent(article.article_group_key)}`, location.pathname === "/articles" ? { state: { from: location.pathname + location.search } } : undefined);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") { setIsOpen(false); return; }
    if (!isOpen || selectableCount === 0) {
      if (event.key === "ArrowDown" && selectableCount > 0) {
        setIsOpen(true);
        event.preventDefault();
      }
      return;
    }
    if (event.key === "ArrowDown") {
      setHighlight((current) => (current + 1) % selectableCount);
      event.preventDefault();
    } else if (event.key === "ArrowUp") {
      setHighlight((current) => (current - 1 + selectableCount) % selectableCount);
      event.preventDefault();
    } else if (event.key === "Enter") {
      const picked = items[highlight];
      if (picked) {
        selectArticle(picked);
        event.preventDefault();
      } else if (documentItems[highlight - items.length]) {
        selectDocument(documentItems[highlight - items.length].beleg_id);
        event.preventDefault();
      }
    } else if (event.key === "Escape") {
      setIsOpen(false);
    }
  }

  const showDropdown = isOpen && currentQuery.length > 0;

  return (
    <div ref={containerRef} className="relative">
      {prominent ? (
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute left-4 top-[22px] z-10 h-5 w-5 -translate-y-1/2 text-slate-400"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
      ) : null}
      <input
        ref={inputRef}
        type="search"
        aria-label={ariaLabel}
        aria-keyshortcuts={enableShortcut ? "Meta+K Control+K" : undefined}
        placeholder={placeholder}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        className={
          prominent
            ? "h-11 w-full rounded-lg border border-slate-300 bg-white pl-11 pr-16 text-base text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            : "h-9 w-full rounded-md border border-slate-300 bg-white pl-3 pr-12 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none"
        }
        aria-autocomplete="list"
        aria-expanded={showDropdown}
      />
      {!query && enableShortcut ? (
        <kbd
          aria-hidden
          className={`pointer-events-none absolute top-1/2 -translate-y-1/2 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] font-medium text-slate-500 ${
            prominent ? "right-3" : "right-2"
          }`}
        >
          {SHORTCUT_LABEL}
        </kbd>
      ) : null}
      {showDropdown && (
        <div
          className={
            prominent
              ? "absolute left-0 z-20 mt-1 max-h-96 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg"
              : "absolute right-0 z-20 mt-1 max-h-96 w-screen max-w-[calc(100vw-2rem)] overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg sm:w-[28rem]"
          }
        >
          <p className="border-b border-slate-100 px-3 py-2 text-xs text-slate-600">
            Artikelnummern und Beschreibungen{belegeSearch ? " · Belegnummern, Großhändler und Besteller" : " · ohne Zeitraumfilter"}
          </p>
          {busy && <p role="status" className="px-3 py-3 text-sm text-slate-600">Suche …</p>}
          {hasError && !busy && <div role="alert" className="px-3 py-3 text-sm text-slate-600">
            <p>{(error instanceof ApiError && error.status === 403) || (documents.error instanceof ApiError && documents.error.status === 403) ? "Für einen Teil dieser Suche fehlt dir die Berechtigung." : "Die Suche konnte nicht vollständig geladen werden."}</p>
            <button type="button" className="mt-2 h-9 rounded-md border border-slate-200 px-3" onClick={() => { if (isError) void refetch(); if (belegeSearch && documents.isError) void documents.refetch(); }}>Erneut versuchen</button>
          </div>}
          {!busy && !hasError && items.length === 0 && documentItems.length === 0 && <div role="status" className="px-3 py-3 text-sm text-slate-600">
            <p className="font-medium text-slate-900">Keine passenden {belegeSearch ? "Artikel oder Belege" : "Artikel"} gefunden.</p>
            <p className="mt-1">Versuche einen Teil der Nummer oder einen kürzeren Begriff aus der Beschreibung.</p>
            <button type="button" className="mt-3 h-9 rounded-md border border-slate-200 bg-white px-3 font-medium text-slate-700" onClick={editQuery}>Suchbegriff ändern</button>
          </div>}
          {!busy && items.length > 0 && <p className="px-3 pt-3 text-xs font-semibold text-slate-600">Artikel</p>}
          {!busy && items.map((article, index) => (
            <button
              data-search-option={index}
              key={article.article_group_key}
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() => {
                selectArticle(article);
              }}
              onMouseEnter={() => {
                setHighlight(index);
              }}
              className={`block w-full px-3 py-2 text-left text-sm ${
                index === highlight ? "bg-slate-100" : "bg-white"
              } hover:bg-slate-100`}
            >
              <ArticleSuggestionContent
                article={article}
                activityPeriod={data?.activity_period}
              />
            </button>
          ))}
          {!busy && documentItems.length > 0 && <div className="border-t border-slate-200" role="group" aria-label="Belegtreffer">
            <p className="px-3 pt-3 text-xs font-semibold text-slate-600">Belege</p>
            {documentItems.map((document, index) => <button data-search-option={items.length + index} key={document.beleg_id} type="button" onMouseDown={event => event.preventDefault()} onMouseEnter={() => setHighlight(items.length + index)} onClick={() => selectDocument(document.beleg_id)} className={`block w-full px-3 py-2 text-left text-sm hover:bg-slate-100 ${highlight === items.length + index ? "bg-slate-100" : "bg-white"}`}>
              <span className="block font-medium">{document.doc_type === "gutschrift" ? "Gutschrift" : "Rechnung"} {document.doc_number ?? "Ohne Belegnummer"}</span>
              <span className="text-xs text-slate-600">{document.supplier ?? "Großhändler nicht angegeben"} · {formatDateShort(document.doc_date)}</span>
            </button>)}
            <Link className="block px-3 py-3 text-sm font-medium text-emerald-700 hover:underline" to={`/belege?q=${encodeURIComponent(currentQuery)}`} onClick={() => setIsOpen(false)}>Alle passenden Belege ansehen →</Link>
          </div>}

        </div>
      )}
    </div>
  );
}
